/**
 * 邀友送花瓣活动归因工具（与俱乐部 invite_code 完全独立的两套体系，缓存互不覆盖）
 *
 * - invite_token 为服务端 AES-GCM 加密的不透明串，仅作透传，前端不解析、不展示
 * - 游客打开落地页时捕获，双端缓存 7 天；注册成功后随注册请求提交并清除
 * - 已登录用户不捕获（活动仅对新注册用户归因）
 * - 设备指纹：本地生成一次 UUID 持久化，用于服务端 24h 设备防刷
 */
import { useUserStore } from '@/store'

/** 站内功能关联编码（精选活动 link_code，与后端约定，禁止散落硬编码） */
export const INVITE_PETAL_LINK_CODE = 'INVITE_PETAL'
/** 邀友活动页路由 */
export const INVITE_PETAL_ROUTE = '/invite/petal'
/** URL 查询参数名（后端约定，禁止散落硬编码） */
export const INVITE_PETAL_QUERY_KEY = 'invite_token'
const LS_TOKEN_KEY = 'aries_invite_petal_token'
/** 设备指纹存储键 */
const LS_DEVICE_KEY = 'aries_device_id'
/** token 有效期 7 天 */
const TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000
/** 服务端 token：IP1. 前缀 + base64url（字母数字 _ - .），最长 96 */
const TOKEN_REGEX = /^IP1\.[A-Za-z0-9_-]{8,92}$/

interface TokenCache {
  token: string
  expireAt: number
}

function isValidToken(v: unknown): v is string {
  return typeof v === 'string' && TOKEN_REGEX.test(v.trim())
}

/** 供外部（如俱乐部资料卡二维码拼装）复用同一套 token 格式校验 */
export function isValidInviteToken(v: unknown): v is string {
  return isValidToken(v)
}

function readTokenCache(): TokenCache | null {
  try {
    const raw = localStorage.getItem(LS_TOKEN_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as Partial<TokenCache>
    if (!isValidToken(data.token) || typeof data.expireAt !== 'number') return null
    if (Date.now() > data.expireAt) return null
    return { token: data.token.trim(), expireAt: data.expireAt }
  } catch {
    return null
  }
}

/**
 * 应用启动 / 路由切换时捕获链接中的 invite_token（hash query 与普通 query 均支持）。
 * 已登录用户忽略；非法格式忽略（无效 token 后端也会直接丢弃）。
 */
export function captureInvitePetalToken(): void {
  try {
    const userStore = useUserStore()
    if (userStore.isLogin) return

    const params = new URLSearchParams(window.location.search || '')
    let token = params.get(INVITE_PETAL_QUERY_KEY) || ''
    if (!token && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.slice(window.location.hash.indexOf('?') + 1)
      token = new URLSearchParams(hashQuery).get(INVITE_PETAL_QUERY_KEY) || ''
    }
    token = (token || '').trim()
    if (!isValidToken(token)) return

    const data: TokenCache = { token, expireAt: Date.now() + TOKEN_TTL_MS }
    localStorage.setItem(LS_TOKEN_KEY, JSON.stringify(data))
  } catch (e) {
    // 隐私模式 / 存储禁用静默降级，不影响浏览
    console.warn('[invitePetal] token 缓存失败', e)
  }
}

/** 注册时读取有效 token；过期/非法返回 null 并清理 */
export function getInvitePetalToken(): string | null {
  const cache = readTokenCache()
  if (cache?.token) return cache.token
  try {
    localStorage.removeItem(LS_TOKEN_KEY)
  } catch { /* ignore */ }
  return null
}

/** 注册成功后清除（归因已落库，无需继续携带） */
export function clearInvitePetalToken(): void {
  try {
    localStorage.removeItem(LS_TOKEN_KEY)
  } catch { /* ignore */ }
}

/**
 * 设备指纹：首次访问生成 UUID 并持久化（卸载/清缓存后重新生成，符合隐私最小化）。
 * 仅用于邀友活动 24h 设备维度防刷，不采集任何系统信息。
 */
export function getDeviceId(): string {
  try {
    const existed = localStorage.getItem(LS_DEVICE_KEY)
    if (existed && /^[a-f0-9-]{36}$/.test(existed)) return existed
    const generated = generateUuid()
    localStorage.setItem(LS_DEVICE_KEY, generated)
    return generated
  } catch {
    // 存储不可用时每次生成临时值（防刷能力降级，不影响注册）
    return generateUuid()
  }
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    try {
      return crypto.randomUUID()
    } catch { /* fallthrough */ }
  }
  // 兜底：RFC4122 v4 风格随机串
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0')
  return `${hex()}${hex()}-${hex()}-4${hex().slice(1)}-${hex()}-${hex()}${hex()}${hex()}`
}

/** 落地页绝对地址（hash 路由），微信内签名/扫码均要求公网绝对 URL */
export function buildLandingUrl(inviteToken: string): string {
  const base = import.meta.env.BASE_URL || '/'
  const origin = window.location.origin
  const normalizedBase = base.endsWith('/') ? base : `${base}/`
  return `${origin}${normalizedBase}#/invite/landing?${INVITE_PETAL_QUERY_KEY}=${encodeURIComponent(inviteToken)}`
}

/** 站点首页绝对地址（活动结束后分享仅指向首页） */
export function buildHomeUrl(): string {
  const base = import.meta.env.BASE_URL || '/'
  const normalizedBase = base.endsWith('/') ? base : `${base}/`
  return `${window.location.origin}${normalizedBase}#/home`
}
